import type { App, HomeyAPI, ItemProps } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type AppProps = ItemProps<App>;

type AppsData = { [key: string]: App };
type AppsById = { [key: string]: AppProps };

interface State {
  data: AppsData | null;
  byId: AppsById | null;
  isLoading: boolean;
  error: unknown;
}

export type AppsStoreState = State;

export class AppsStore extends BaseApiStore<State, App> {
  constructor() {
    super('apps');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.apps });

      if (abortController.signal.aborted) return;

      const appsPromise = api.apps.getApps({ $cache: cache });
      const [appsData] = await Promise.all([appsPromise]);

      if (abortController.signal.aborted) return;

      const apps = Object.values(appsData);

      const data: AppsData = {};
      const byId: AppsById = {};

      for (const appInstance of apps) {
        const appReference = appInstance.getProps();

        data[appReference.id] = appInstance;
        byId[appReference.id] = appReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.apps.on('app.create', this.handleCreate);
    api.apps.on('app.update', this.handleUpdate);
    api.apps.on('app.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.apps.off('app.create', this.handleCreate);
    api.apps.off('app.update', this.handleUpdate);
    api.apps.off('app.delete', this.handleDelete);
  }

  getInstanceFromCache(app: { id: string }) {
    return this.getCache()[app.id];
  }

  getCache(): Readonly<Record<string, App>> {
    const api = this.getApi();

    return api.apps.getCachedItems('app');
  }

  handleCreate = (createdApp: App) => {
    this.DEBUG && console.info(`create:app:${createdApp.id}`);
    const state = this.get();
    const appInstance = this.getInstanceFromCache(createdApp);

    if (appInstance == null) return;

    const appReference = appInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdApp.id]: appInstance,
      },
      byId: {
        ...state.byId,
        [createdApp.id]: appReference,
      },
    });
  };

  handleUpdate = (updatedApp: App) => {
    this.DEBUG && console.info(`update:app:${updatedApp.id}`);

    this.addToQueue(updatedApp.id, updatedApp);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedApp] of Object.entries(updateQueue)) {
        const appInstance = this.getInstanceFromCache(queuedApp);

        if (state.data == null) {
          this.DEBUG && console.info(`update:app:missing-data`);
          continue;
        }

        if (appInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:app:unknown:${id}`);
          continue;
        }

        const appReference = appInstance.getProps();

        nextById[id] = appReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:app:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedApp: Pick<App, 'id'>) => {
    this.DEBUG && console.info(`delete:app:${deletedApp.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:app:missing-data`);
      return;
    }

    const appInstance = state.data[deletedApp.id];

    if (appInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[appInstance.id];
    delete nextById[appInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const apps = Object.values(cache ?? {});
    const data: AppsData = {};
    const byId: AppsById = {};

    for (const appInstance of apps) {
      const appReference = appInstance.getProps();
      data[appReference.id] = appInstance;
      byId[appReference.id] = appReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const appsStore = new AppsStore();
